// @ts-check
/** Irregular drifting atoms fill an enclosed nanoscale cavity.
 * @param {CanvasRenderingContext2D} c @param {{width:number,height:number,floor:number}} v
 * @param {number} camera @param {number} time @param {import('./boot-sector.js').Palette} p @param {boolean} reduced
 */
export function paintQuantum(c, v, camera, time, p, reduced) {
  c.save(); c.fillStyle = p.void; c.fillRect(0, 0, v.width, v.height);
  const t = reduced ? 0 : time;
  const glow = c.createRadialGradient(v.width * .62, v.height * .4, 4, v.width * .62, v.height * .4, v.width * .7);
  glow.addColorStop(0, p.violet); glow.addColorStop(.55, p.haze); glow.addColorStop(1, p.void);
  c.globalAlpha = .36; c.fillStyle = glow; c.fillRect(0, 0, v.width, v.height); c.globalAlpha = 1;
  // Slow interference membranes cross the cavity behind the separate floating atoms.
  for (let band = 0; band < 3; band++) {
    c.beginPath();
    for (let x = -40; x <= v.width + 40; x += 20) {
      const phase = (x + camera * .15) / (150 + band * 55);
      const y = v.height * (.24 + band * .24) + Math.sin(phase + t * .18 + band * 2) * 55;
      if (x === -40) c.moveTo(x, y); else c.lineTo(x, y);
    }
    c.strokeStyle = band === 1 ? p.cyan : p.violet; c.globalAlpha = .08; c.lineWidth = 28; c.stroke();
    c.globalAlpha = .18; c.lineWidth = 1; c.stroke();
  }
  c.globalAlpha = 1;
  // Stable seeded positions prevent popping; independent lively drift removes any lattice.
  const noise = (/** @type {number} */ seed) => { const n = Math.sin(seed * 127.1 + 311.7) * 43758.5453; return n - Math.floor(n); };
  // Local, softly fading novas travel with the background, never flashing the screen.
  if (!reduced) {
    const cycle = Math.floor(t / 2.8), age = t % 2.8;
    if (age < 1.6) {
      const strength = Math.sin(age / 1.6 * Math.PI) ** 2;
      const x = v.width * (.12 + noise(cycle + 500) * .76);
      const y = v.height * (.18 + noise(cycle + 600) * .55);
      const radius = 12 + age * 34;
      const nova = c.createRadialGradient(x, y, 0, x, y, radius);
      nova.addColorStop(0, p.light); nova.addColorStop(.12, p.cyan);
      nova.addColorStop(.4, p.violet); nova.addColorStop(1, 'transparent');
      c.globalAlpha = strength * .55; c.fillStyle = nova;
      c.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      c.globalAlpha = strength * .3; c.strokeStyle = p.cyan; c.lineWidth = 1;
      c.beginPath(); c.ellipse(x, y, radius, radius * .28, -.4, 0, Math.PI * 2); c.stroke();
    }
  }
  c.globalAlpha = 1;
  for (let layer = 0; layer < 2; layer++) {
    const step = 360, offset = camera * (layer ? .28 : .11);
    const first = Math.floor(offset / step) - 2;
    for (let col = first; col <= first + Math.ceil(v.width / step) + 4; col++) {
      for (let atom = 0; atom < 4; atom++) {
        const seed = col * 31 + atom * 7 + layer * 1009;
        const phase = noise(seed + 1) * Math.PI * 2, speed = .55 + noise(seed + 2) * .65;
        const x = col * step - offset + noise(seed + 3) * step + Math.sin(t * speed + phase) * (36 + noise(seed + 4) * 44);
        const y = 45 + noise(seed + 5) * (v.height - 90) + Math.cos(t * speed * .73 + phase * 2) * 52;
        const radius = (layer ? 14 : 28) + noise(seed + 6) * 33;
        if (x + radius < 0 || x - radius > v.width) continue;
        c.save(); c.translate(x, y); c.rotate(phase + Math.sin(t * .1 + seed) * .3);
        const color = noise(seed + 7) > .5 ? p.cyan : p.violet;
        c.fillStyle = color; c.globalAlpha = layer ? .32 : .65;
        c.beginPath(); c.arc(0, 0, radius * .075, 0, Math.PI * 2); c.fill();
        for (let orbit = 0; orbit < 2 + (atom % 2); orbit++) {
          c.save(); c.rotate(noise(seed + 10 + orbit) * Math.PI);
          const r = radius * (.72 + noise(seed + 20 + orbit) * .45), squash = .25 + noise(seed + 30 + orbit) * .3;
          c.strokeStyle = color; c.lineWidth = .8; c.globalAlpha = layer ? .11 : .3;
          c.beginPath(); c.ellipse(0, 0, r, r * squash, 0, 0, Math.PI * 2); c.stroke();
          const a = phase + t * (.35 + noise(seed + 40 + orbit) * .8) * (orbit % 2 ? -1 : 1);
          c.globalAlpha = layer ? .4 : .85; c.fillStyle = p.light;
          c.beginPath(); c.arc(Math.cos(a) * r, Math.sin(a) * r * squash, layer ? 1.2 : 2, 0, Math.PI * 2); c.fill(); c.restore();
        }
        c.restore();
      }
    }
  }
  // A scale ruler keeps the surprising change of scale visible during play.
  c.globalAlpha = .65; c.strokeStyle = p.cyan; c.lineWidth = 1;
  c.beginPath(); c.moveTo(34, v.height * .35); c.lineTo(134, v.height * .35); c.stroke();
  for (const x of [34, 84, 134]) c.fillRect(x, v.height * .35 - 4, 1, 8);
  c.fillStyle = p.light; c.font = '11px monospace'; c.textAlign = 'left'; c.fillText('2 nm / QUANTUM SPACE', 34, v.height * .35 - 14);
  c.restore();
}

/** Faceted islands and prize crystals share the room's material, with a readable landing edge.
 * @param {CanvasRenderingContext2D} c @param {{x:number,width:number,height:number,kind?:string,used?:boolean}} s
 * @param {number} y @param {import('./terrain.js').Palette} p
 */
export function paintQuantumIsland(c, s, y, p) {
  const node = s.kind === 'node', w = s.width, h = s.height;
  c.save(); c.translate(s.x, y);
  c.fillStyle = p.panel; c.strokeStyle = s.used ? p.edge : p.violet; c.lineWidth = 1;
  c.beginPath(); c.moveTo(0, 0); c.lineTo(w, 0); c.lineTo(w * .84, h);
  c.lineTo(w * .52, h + (node ? 6 : 18)); c.lineTo(w * .18, h); c.closePath(); c.fill(); c.stroke();
  c.globalAlpha = .45; c.fillStyle = p.violet;
  c.beginPath(); c.moveTo(w * .2, 0); c.lineTo(w * .52, h + 14); c.lineTo(w * .68, 0); c.fill();
  c.globalAlpha = 1; c.strokeStyle = p.cyan; c.lineWidth = 2;
  c.beginPath(); c.moveTo(0, 0); c.lineTo(w, 0); c.stroke();
  if (node && !s.used) {
    c.fillStyle = p.gold; c.beginPath(); c.moveTo(w / 2, 5); c.lineTo(w / 2 + 6, h / 2); c.lineTo(w / 2, h - 5); c.lineTo(w / 2 - 6, h / 2); c.closePath(); c.fill();
  }
  c.restore();
}

/** A cloud of expanding matter marks the passage back to ordinary scale.
 * @param {CanvasRenderingContext2D} c @param {number} x @param {number} time
 * @param {import('./terrain.js').Palette} p @param {boolean} reduced @param {boolean} active
 */
export function paintQuantumReturn(c, x, time, p, reduced, active) {
  const t = reduced ? 0 : time;
  c.save(); c.translate(x, -62);
  // Overlapping soft lobes give the field a cloud silhouette rather than a frame.
  for (let n = 0; n < 9; n++) {
    const angle = n * 2.399 + t * .24;
    const px = Math.cos(angle) * (20 + n % 3 * 9);
    const py = Math.sin(angle) * (30 + n % 2 * 12);
    const radius = 34 + Math.sin(t * 1.1 + n) * 4 + (active ? 8 : 0);
    const fog = c.createRadialGradient(px, py, 1, px, py, radius);
    fog.addColorStop(0, n % 3 ? p.cyan : p.violet); fog.addColorStop(.45, p.violet); fog.addColorStop(1, 'transparent');
    c.globalAlpha = n % 3 ? .18 : .3; c.fillStyle = fog;
    c.fillRect(px - radius, py - radius, radius * 2, radius * 2);
  }
  // Broken orbital arcs widen around the nucleus, suggesting matter changing scale.
  for (let n = 0; n < 3; n++) {
    c.globalAlpha = .45; c.strokeStyle = n === 1 ? p.violet : p.cyan; c.lineWidth = 1.2;
    c.beginPath(); c.ellipse(0, 0, 27 + n * 15, 38 + n * 12, Math.sin(t * .3 + n) * .5,
      t * .45 + n * 2, t * .45 + n * 2 + Math.PI * 1.35); c.stroke();
  }
  for (let n = 0; n < 22; n++) {
    const phase = (n / 22 + t * .22) % 1;
    const angle = n * 2.399 + t * .8 + phase * 2;
    const radius = 6 + phase * 57;
    c.globalAlpha = Math.sin(phase * Math.PI) * .85;
    c.fillStyle = n % 4 ? p.cyan : p.light;
    c.beginPath(); c.arc(Math.cos(angle) * radius, Math.sin(angle) * radius * 1.08, 1 + phase * 2.2, 0, Math.PI * 2); c.fill();
  }
  const core = c.createRadialGradient(0, 0, 0, 0, 0, 23);
  core.addColorStop(0, p.light); core.addColorStop(.2, p.cyan); core.addColorStop(1, 'transparent');
  c.globalAlpha = .7; c.fillStyle = core; c.fillRect(-23, -23, 46, 46);
  c.globalAlpha = .8; c.fillStyle = p.light; c.textAlign = 'center'; c.font = '8px monospace';
  c.fillText('RETURN TO SCALE', 0, 82);
  c.restore();
}
